import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { settingsApi, type DownloadCategories } from "@/api/settings";
import {
  formatSpeed,
  nzbApi,
  POST_PROCESSING_LABELS,
  type CreateNewsServer,
  type NewsServer,
  type NzbCategory,
  type NzbGeneral,
} from "@/api/nzb";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, NumberInput, Select, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Draft model
// ---------------------------------------------------------------------------

type DraftStatus = "clean" | "created" | "updated" | "deleted";

interface ServerDraft extends NewsServer {
  _status: DraftStatus;
  /** Negative synthetic id used while a created row hasn't been persisted. */
  _tempId?: number;
  /** Plain-text password the user typed. Only sent on save when set. */
  _newPassword?: string;
}

interface CategoryDraft extends NzbCategory {
  _status: DraftStatus;
  /** The original (server-side) name. Categories are keyed by name and the
   *  backend doesn't currently support renaming, so this is informational
   *  only — used as the lookup key when issuing the update PUT. */
  _originalName?: string;
}

type TabKey = "servers" | "categories" | "general";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const stripDraftFields = <T extends Record<string, unknown>>(d: T) => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(d)) {
    if (k.startsWith("_")) continue;
    out[k] = v;
  }
  return out;
};

const sameGeneral = (a: NzbGeneral, b: NzbGeneral) =>
  a.speed_limit_bps === b.speed_limit_bps &&
  a.max_active_downloads === b.max_active_downloads &&
  a.history_retention_count === b.history_retention_count;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ServerEditingState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; tempId: number | null; id: number | null };

type CategoryEditingState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; key: string };

export function NzbDownloading() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const serversQ = useQuery({
    queryKey: ["nzb", "servers"],
    queryFn: nzbApi.servers.list,
  });
  const categoriesQ = useQuery({
    queryKey: ["nzb", "categories"],
    queryFn: nzbApi.categories.list,
  });
  const generalQ = useQuery({
    queryKey: ["nzb", "general"],
    queryFn: nzbApi.general.get,
  });

  const [activeTab, setActiveTab] = useState<TabKey>("servers");
  const [saving, setSaving] = useState(false);

  // Drafts (re-seeded any time the underlying queries succeed). When the
  // user has uncommitted changes we don't want a background refetch to nuke
  // them — so we only re-seed when there are no pending drafts in that
  // resource.
  const [serverDrafts, setServerDrafts] = useState<ServerDraft[]>([]);
  const [categoryDrafts, setCategoryDrafts] = useState<CategoryDraft[]>([]);
  const [generalDraft, setGeneralDraft] = useState<NzbGeneral | null>(null);
  // The pristine value so we can compute "dirty" + reset on Discard.
  const [generalLoaded, setGeneralLoaded] = useState<NzbGeneral | null>(null);

  useEffect(() => {
    if (!serversQ.data) return;
    const hasPending = serverDrafts.some((d) => d._status !== "clean");
    if (hasPending) return;
    setServerDrafts(
      serversQ.data.map((s) => ({ ...s, _status: "clean" as const })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serversQ.data]);

  useEffect(() => {
    if (!categoriesQ.data) return;
    const hasPending = categoryDrafts.some((d) => d._status !== "clean");
    if (hasPending) return;
    setCategoryDrafts(
      categoriesQ.data.map((c) => ({
        ...c,
        _status: "clean" as const,
        _originalName: c.name,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesQ.data]);

  useEffect(() => {
    if (!generalQ.data) return;
    if (generalDraft && generalLoaded && !sameGeneral(generalDraft, generalLoaded)) {
      // User has unsaved general changes; don't clobber.
      return;
    }
    setGeneralDraft(generalQ.data);
    setGeneralLoaded(generalQ.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalQ.data]);

  // Counts ------------------------------------------------------------------

  const serverPending = useMemo(
    () => serverDrafts.filter((d) => d._status !== "clean").length,
    [serverDrafts],
  );
  const categoryPending = useMemo(
    () => categoryDrafts.filter((d) => d._status !== "clean").length,
    [categoryDrafts],
  );
  const generalDirty =
    !!generalDraft && !!generalLoaded && !sameGeneral(generalDraft, generalLoaded);
  const totalPending = serverPending + categoryPending + (generalDirty ? 1 : 0);

  // Discard / Save ---------------------------------------------------------

  const discard = () => {
    if (totalPending === 0) return;
    if (!confirm(`Discard ${totalPending} pending change(s)?`)) return;
    if (serversQ.data)
      setServerDrafts(
        serversQ.data.map((s) => ({ ...s, _status: "clean" as const })),
      );
    if (categoriesQ.data)
      setCategoryDrafts(
        categoriesQ.data.map((c) => ({
          ...c,
          _status: "clean" as const,
          _originalName: c.name,
        })),
      );
    if (generalLoaded) setGeneralDraft(generalLoaded);
  };

  const saveAll = async () => {
    if (totalPending === 0) return;
    setSaving(true);
    try {
      // ---- Servers ------------------------------------------------------
      for (const d of serverDrafts) {
        if (d._status === "clean") continue;
        if (d._status === "deleted") {
          if (d._tempId !== undefined) continue; // never persisted
          await nzbApi.servers.remove(d.id);
        } else if (d._status === "created") {
          await nzbApi.servers.create({
            name: d.name,
            host: d.host,
            port: d.port,
            ssl: d.ssl,
            enabled: d.enabled,
            priority: d.priority,
            connections: d.connections,
            username: d.username,
            password: d._newPassword ?? null,
          });
        } else if (d._status === "updated") {
          await nzbApi.servers.update(d.id, {
            name: d.name,
            host: d.host,
            port: d.port,
            ssl: d.ssl,
            enabled: d.enabled,
            priority: d.priority,
            connections: d.connections,
            username: d.username,
            // undefined => keep existing; only send when user typed one.
            password: d._newPassword ? d._newPassword : undefined,
          });
        }
      }

      // ---- Categories ---------------------------------------------------
      for (const d of categoryDrafts) {
        if (d._status === "clean") continue;
        if (d._status === "deleted") {
          // Only DELETE if it was ever persisted.
          if (d._originalName) await nzbApi.categories.remove(d._originalName);
        } else if (d._status === "created") {
          await nzbApi.categories.create({
            name: d.name,
            output_dir: d.output_dir,
            post_processing: d.post_processing,
          });
        } else if (d._status === "updated") {
          // Name is the PK and immutable on edit — use the original.
          await nzbApi.categories.update(d._originalName ?? d.name, {
            output_dir: d.output_dir,
            post_processing: d.post_processing,
          });
        }
      }

      // ---- General ------------------------------------------------------
      if (generalDirty && generalDraft) {
        await nzbApi.general.update(generalDraft);
      }

      toast(`Saved ${totalPending} change(s)`, "success");
      // Reload everything so the drafts get re-seeded with the canonical
      // server-side state.
      qc.invalidateQueries({ queryKey: ["nzb", "servers"] });
      qc.invalidateQueries({ queryKey: ["nzb", "categories"] });
      qc.invalidateQueries({ queryKey: ["nzb", "general"] });
      qc.invalidateQueries({ queryKey: ["nzb", "status"] });
      // Force a re-seed by clearing pending markers locally too — any rows
      // that did persist will be replaced when the queries refetch.
      setServerDrafts((rows) =>
        rows
          .filter((r) => r._status !== "deleted")
          .map((r) => ({ ...r, _status: "clean" as const, _newPassword: undefined })),
      );
      setCategoryDrafts((rows) =>
        rows
          .filter((r) => r._status !== "deleted")
          .map((r) => ({
            ...r,
            _status: "clean" as const,
            _originalName: r.name,
          })),
      );
      if (generalDirty && generalDraft) setGeneralLoaded(generalDraft);
    } catch (err) {
      // Stop on first failure — leave remaining drafts untouched so the
      // user can fix the offending one and retry. The successful ones
      // will get re-seeded on the next query reload.
      toast(err instanceof Error ? err.message : "Save failed", "error");
      qc.invalidateQueries({ queryKey: ["nzb", "servers"] });
      qc.invalidateQueries({ queryKey: ["nzb", "categories"] });
      qc.invalidateQueries({ queryKey: ["nzb", "general"] });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="NZB Downloading"
        description="Configure Usenet news servers, NZB categories, and general downloader behaviour."
        actions={
          <SaveBar
            pending={totalPending}
            saving={saving}
            onSave={saveAll}
            onDiscard={discard}
          />
        }
      />

      <Tabs
        active={activeTab}
        onChange={setActiveTab}
        counts={{
          servers: serverPending,
          categories: categoryPending,
          general: generalDirty ? 1 : 0,
        }}
      />

      {activeTab === "servers" && (
        <ServersTab
          drafts={serverDrafts}
          setDrafts={setServerDrafts}
          loading={serversQ.isLoading}
          error={serversQ.isError ? serversQ.error : null}
        />
      )}
      {activeTab === "categories" && (
        <CategoriesTab
          drafts={categoryDrafts}
          setDrafts={setCategoryDrafts}
          loading={categoriesQ.isLoading}
          error={categoriesQ.isError ? categoriesQ.error : null}
        />
      )}
      {activeTab === "general" && (
        <GeneralTab
          draft={generalDraft}
          setDraft={setGeneralDraft}
          loading={generalQ.isLoading}
          error={generalQ.isError ? generalQ.error : null}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SaveBar / Tabs
// ---------------------------------------------------------------------------

function SaveBar({
  pending,
  saving,
  onSave,
  onDiscard,
}: {
  pending: number;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {pending > 0 && (
        <Button
          variant="ghost"
          size="md"
          onClick={onDiscard}
          disabled={saving}
        >
          Discard
        </Button>
      )}
      <Button onClick={onSave} loading={saving} disabled={pending === 0}>
        {pending > 0 ? `Save (${pending} pending)` : "Save"}
      </Button>
    </div>
  );
}

function Tabs({
  active,
  onChange,
  counts,
}: {
  active: TabKey;
  onChange: (k: TabKey) => void;
  counts: Record<TabKey, number>;
}) {
  const tabs: { key: TabKey; label: string }[] = [
    { key: "servers", label: "News Servers" },
    { key: "categories", label: "Categories" },
    { key: "general", label: "General" },
  ];
  return (
    <div className="flex gap-1 border-b border-line">
      {tabs.map((t) => {
        const isActive = t.key === active;
        const n = counts[t.key];
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={cn(
              "-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition",
              isActive
                ? "border-accent font-medium text-foreground"
                : "border-transparent text-foreground/60 hover:text-foreground",
            )}
          >
            <span>{t.label}</span>
            {n > 0 && (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                  isActive
                    ? "bg-accent/20 text-accent"
                    : "bg-amber-500/20 text-amber-300",
                )}
              >
                {n}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ServersTab
// ---------------------------------------------------------------------------

function rowKey(d: ServerDraft): string {
  return d._tempId !== undefined ? `tmp:${d._tempId}` : `id:${d.id}`;
}

function ServersTab({
  drafts,
  setDrafts,
  loading,
  error,
}: {
  drafts: ServerDraft[];
  setDrafts: React.Dispatch<React.SetStateAction<ServerDraft[]>>;
  loading: boolean;
  error: unknown;
}) {
  const [editing, setEditing] = useState<ServerEditingState>({ mode: "closed" });
  const [testingKey, setTestingKey] = useState<string | null>(null);
  const { toast } = useToast();

  const visibleDrafts = drafts; // includes deleted (struck-through)

  const openCreate = () => setEditing({ mode: "create" });
  const openEdit = (d: ServerDraft) =>
    setEditing({
      mode: "edit",
      tempId: d._tempId ?? null,
      id: d._tempId === undefined ? d.id : null,
    });

  const findEditingDraft = (): ServerDraft | undefined => {
    if (editing.mode !== "edit") return undefined;
    return drafts.find((d) =>
      editing.tempId !== null
        ? d._tempId === editing.tempId
        : d.id === editing.id,
    );
  };

  const onModalSave = (next: ServerDraft) => {
    setDrafts((rows) => {
      if (editing.mode === "create") {
        return [...rows, next];
      }
      return rows.map((r) =>
        rowKey(r) === rowKey(next)
          ? {
              ...next,
              _status: r._status === "created" ? "created" : "updated",
            }
          : r,
      );
    });
    setEditing({ mode: "closed" });
  };

  const markDeleted = (d: ServerDraft) => {
    setDrafts((rows) => {
      // If it was a created-but-not-saved row, drop it entirely.
      if (d._status === "created") {
        return rows.filter((r) => rowKey(r) !== rowKey(d));
      }
      return rows.map((r) =>
        rowKey(r) === rowKey(d) ? { ...r, _status: "deleted" } : r,
      );
    });
  };

  const restore = (d: ServerDraft) => {
    setDrafts((rows) =>
      rows.map((r) =>
        rowKey(r) === rowKey(d)
          ? { ...r, _status: r._newPassword !== undefined ? "updated" : "clean" }
          : r,
      ),
    );
  };

  const test = async (d: ServerDraft) => {
    if (d._tempId !== undefined) {
      toast("Save first, or use Test from the edit modal.", "info");
      return;
    }
    const key = rowKey(d);
    setTestingKey(key);
    try {
      const res = await nzbApi.servers.test(d.id);
      toast(
        res.ok ? `${d.name} reachable` : `${d.name} failed: ${res.message ?? "unknown"}`,
        res.ok ? "success" : "error",
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : "Test failed", "error");
    } finally {
      setTestingKey(null);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="max-w-2xl text-sm text-foreground/60">
          Usenet (NNTP) providers used by the embedded NZB downloader. Try multiple — the
          highest priority server is queried first.
        </p>
        <Button onClick={openCreate} size="sm">
          Add server
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : error ? (
        <EmptyState
          title="NZB downloader is unavailable"
          description={
            error instanceof Error ? `Backend said: ${error.message}` : undefined
          }
        />
      ) : visibleDrafts.length === 0 ? (
        <EmptyState
          title="No news servers configured"
          description="Add at least one Usenet provider to start downloading NZBs."
          action={<Button onClick={openCreate}>Add server</Button>}
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Endpoint</th>
                <th className="px-4 py-2 font-medium">Conns</th>
                <th className="px-4 py-2 font-medium">Priority</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {visibleDrafts.map((row) => {
                const key = rowKey(row);
                const deleted = row._status === "deleted";
                return (
                  <tr
                    key={key}
                    className={cn(
                      "border-t border-line",
                      deleted && "text-foreground/40",
                    )}
                  >
                    <td
                      className={cn(
                        "px-4 py-2 font-medium",
                        deleted && "line-through",
                        row._status === "created" && "italic",
                      )}
                    >
                      {row.name}
                      {!row.enabled && (
                        <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-foreground/60">
                          disabled
                        </span>
                      )}
                      <DraftBadge status={row._status} />
                    </td>
                    <td className={cn("px-4 py-2 font-mono text-xs text-foreground/60", deleted && "line-through")}>
                      {row.ssl ? "nntps" : "nntp"}://{row.host}:{row.port}
                    </td>
                    <td className="px-4 py-2 text-xs text-foreground/70 tabular-nums">
                      {row.connections}
                    </td>
                    <td className="px-4 py-2 text-xs text-foreground/70 tabular-nums">
                      {row.priority}
                    </td>
                    <td className="space-x-2 px-4 py-2 text-right">
                      {deleted ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => restore(row)}
                        >
                          Restore
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={
                              row._tempId !== undefined || testingKey === key
                            }
                            loading={testingKey === key}
                            onClick={() => test(row)}
                          >
                            Test
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openEdit(row)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => markDeleted(row)}
                          >
                            Remove
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ServerEditModal
        editing={editing}
        existing={findEditingDraft()}
        onClose={() => setEditing({ mode: "closed" })}
        onSave={onModalSave}
      />
    </section>
  );
}

function DraftBadge({ status }: { status: DraftStatus }) {
  if (status === "clean" || status === "deleted") return null;
  const label = status === "created" ? "(new)" : "(edited)";
  return (
    <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-300">
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ServerEditModal
// ---------------------------------------------------------------------------

interface ServerForm {
  name: string;
  host: string;
  port: number;
  ssl: boolean;
  enabled: boolean;
  priority: number;
  connections: number;
  username: string;
  password: string;
}

const blankServerForm = (): ServerForm => ({
  name: "",
  host: "",
  port: 563,
  ssl: true,
  enabled: true,
  priority: 0,
  connections: 8,
  username: "",
  password: "",
});

const serverFormFromDraft = (d: ServerDraft): ServerForm => ({
  name: d.name,
  host: d.host,
  port: d.port,
  ssl: d.ssl,
  enabled: d.enabled,
  priority: d.priority,
  connections: d.connections,
  username: d.username ?? "",
  // _newPassword survives across edit sessions until save.
  password: d._newPassword ?? "",
});

const formToCreate = (f: ServerForm): CreateNewsServer => ({
  name: f.name,
  host: f.host,
  port: f.port,
  ssl: f.ssl,
  enabled: f.enabled,
  priority: f.priority,
  connections: f.connections,
  username: f.username.trim() ? f.username : null,
  password: f.password.trim() ? f.password : null,
});

// Synthetic tempId generator. Negative to avoid collision with real ids.
let nextTempId = -1;
const allocTempId = () => {
  const id = nextTempId;
  nextTempId -= 1;
  return id;
};

function ServerEditModal({
  editing,
  existing,
  onClose,
  onSave,
}: {
  editing: ServerEditingState;
  existing: ServerDraft | undefined;
  onClose: () => void;
  onSave: (d: ServerDraft) => void;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState<ServerForm>(blankServerForm());
  const [testing, setTesting] = useState(false);

  const open = editing.mode !== "closed";
  const isEdit = editing.mode === "edit";

  useEffect(() => {
    if (editing.mode === "create") setForm(blankServerForm());
    if (editing.mode === "edit" && existing) setForm(serverFormFromDraft(existing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (editing.mode === "create") {
      const tempId = allocTempId();
      onSave({
        id: 0, // unused — _tempId distinguishes
        name: form.name,
        host: form.host,
        port: form.port,
        ssl: form.ssl,
        enabled: form.enabled,
        priority: form.priority,
        connections: form.connections,
        username: form.username.trim() ? form.username : null,
        _status: "created",
        _tempId: tempId,
        _newPassword: form.password.trim() ? form.password : undefined,
      });
    } else if (editing.mode === "edit" && existing) {
      onSave({
        ...existing,
        name: form.name,
        host: form.host,
        port: form.port,
        ssl: form.ssl,
        enabled: form.enabled,
        priority: form.priority,
        connections: form.connections,
        username: form.username.trim() ? form.username : null,
        _newPassword: form.password.trim()
          ? form.password
          : existing._newPassword,
      });
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const res = await nzbApi.servers.testConfig(formToCreate(form));
      toast(
        res.ok ? "Connection succeeded" : `Connection failed: ${res.message ?? "unknown"}`,
        res.ok ? "success" : "error",
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : "Test failed", "error");
    } finally {
      setTesting(false);
    }
  };

  const passwordPlaceholder = isEdit ? "leave blank to keep existing" : "";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${existing?.name ?? "server"}` : "Add news server"}
      width="lg"
    >
      <form onSubmit={submit} className="grid grid-cols-2 gap-4">
        <Field label="Name">
          <TextInput
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Newsdemon"
          />
        </Field>
        <Field label="Enabled">
          <Select
            value={form.enabled ? "1" : "0"}
            onChange={(e) =>
              setForm((f) => ({ ...f, enabled: e.target.value === "1" }))
            }
          >
            <option value="1">Yes</option>
            <option value="0">No</option>
          </Select>
        </Field>
        <Field label="Host">
          <TextInput
            required
            value={form.host}
            onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
            placeholder="news.example.com"
          />
        </Field>
        <Field label="Port">
          <NumberInput
            required
            value={form.port}
            onChange={(e) =>
              setForm((f) => ({ ...f, port: Number(e.target.value) || 0 }))
            }
          />
        </Field>
        <Field label="SSL">
          <Select
            value={form.ssl ? "1" : "0"}
            onChange={(e) =>
              setForm((f) => ({ ...f, ssl: e.target.value === "1" }))
            }
          >
            <option value="1">Yes (NNTPS)</option>
            <option value="0">No (NNTP)</option>
          </Select>
        </Field>
        <Field label="Connections" hint="Max concurrent NNTP connections.">
          <NumberInput
            required
            min={1}
            value={form.connections}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                connections: Number(e.target.value) || 1,
              }))
            }
          />
        </Field>
        <Field label="Username">
          <TextInput
            value={form.username}
            onChange={(e) =>
              setForm((f) => ({ ...f, username: e.target.value }))
            }
          />
        </Field>
        <Field
          label="Password"
          hint={isEdit ? passwordPlaceholder : undefined}
        >
          <TextInput
            type="password"
            value={form.password}
            placeholder={passwordPlaceholder}
            onChange={(e) =>
              setForm((f) => ({ ...f, password: e.target.value }))
            }
          />
        </Field>
        <Field label="Priority" hint="Lower = tried first.">
          <NumberInput
            value={form.priority}
            onChange={(e) =>
              setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))
            }
          />
        </Field>
        <div className="col-span-2 flex items-center justify-between border-t border-line pt-3">
          <Button type="button" variant="secondary" onClick={runTest} loading={testing}>
            Test connection
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">{isEdit ? "Apply" : "Add"}</Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// CategoriesTab
// ---------------------------------------------------------------------------

function categoryKey(d: CategoryDraft): string {
  return d._originalName ?? `new:${d.name}`;
}

function CategoriesTab({
  drafts,
  setDrafts,
  loading,
  error,
}: {
  drafts: CategoryDraft[];
  setDrafts: React.Dispatch<React.SetStateAction<CategoryDraft[]>>;
  loading: boolean;
  error: unknown;
}) {
  const [editing, setEditing] = useState<CategoryEditingState>({ mode: "closed" });

  const openCreate = () => setEditing({ mode: "create" });
  const openEdit = (d: CategoryDraft) => setEditing({ mode: "edit", key: categoryKey(d) });

  const findEditingDraft = (): CategoryDraft | undefined => {
    if (editing.mode !== "edit") return undefined;
    return drafts.find((d) => categoryKey(d) === editing.key);
  };

  const onModalSave = (next: CategoryDraft) => {
    const current = editing;
    setDrafts((rows) => {
      if (current.mode === "create") {
        return [...rows, next];
      }
      if (current.mode === "edit") {
        return rows.map((r) =>
          categoryKey(r) === current.key
            ? {
                ...next,
                _status: r._status === "created" ? "created" : "updated",
              }
            : r,
        );
      }
      return rows;
    });
    setEditing({ mode: "closed" });
  };

  const markDeleted = (d: CategoryDraft) => {
    setDrafts((rows) => {
      if (d._status === "created") {
        return rows.filter((r) => categoryKey(r) !== categoryKey(d));
      }
      return rows.map((r) =>
        categoryKey(r) === categoryKey(d) ? { ...r, _status: "deleted" } : r,
      );
    });
  };

  const restore = (d: CategoryDraft) => {
    setDrafts((rows) =>
      rows.map((r) =>
        categoryKey(r) === categoryKey(d) ? { ...r, _status: "clean" } : r,
      ),
    );
  };

  return (
    <section className="space-y-6">
      <MediaCategoryMapping availableCategories={drafts.map((d) => d.name)} />

      <div className="flex items-start justify-between gap-4 border-t border-line pt-6">
        <p className="max-w-2xl text-sm text-foreground/60">
          Per-category output directory and post-processing pipeline.
        </p>
        <Button onClick={openCreate} size="sm">
          Add category
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : error ? (
        <EmptyState
          title="NZB downloader is unavailable"
          description={
            error instanceof Error ? `Backend said: ${error.message}` : undefined
          }
        />
      ) : drafts.length === 0 ? (
        <EmptyState
          title="No categories defined"
          description="Categories let you route different release types to different folders."
          action={<Button onClick={openCreate}>Add category</Button>}
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Output dir</th>
                <th className="px-4 py-2 font-medium">Post-processing</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((row) => {
                const key = categoryKey(row);
                const deleted = row._status === "deleted";
                return (
                  <tr
                    key={key}
                    className={cn(
                      "border-t border-line",
                      deleted && "text-foreground/40",
                    )}
                  >
                    <td
                      className={cn(
                        "px-4 py-2 font-medium",
                        deleted && "line-through",
                        row._status === "created" && "italic",
                      )}
                    >
                      {row.name}
                      <DraftBadge status={row._status} />
                    </td>
                    <td className={cn("px-4 py-2 font-mono text-xs text-foreground/60", deleted && "line-through")}>
                      {row.output_dir ?? <span className="text-foreground/40">(default)</span>}
                    </td>
                    <td className={cn("px-4 py-2 text-foreground/70", deleted && "line-through")}>
                      {POST_PROCESSING_LABELS[row.post_processing] ??
                        `Unknown (${row.post_processing})`}
                    </td>
                    <td className="space-x-2 px-4 py-2 text-right">
                      {deleted ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => restore(row)}
                        >
                          Restore
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openEdit(row)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => markDeleted(row)}
                          >
                            Remove
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CategoryEditModal
        editing={editing}
        existing={findEditingDraft()}
        existingNames={new Set(drafts.map((d) => d.name))}
        onClose={() => setEditing({ mode: "closed" })}
        onSave={onModalSave}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// CategoryEditModal
// ---------------------------------------------------------------------------

interface CategoryForm {
  name: string;
  output_dir: string;
  post_processing: number;
}

const blankCategoryForm = (): CategoryForm => ({
  name: "",
  output_dir: "",
  post_processing: 3,
});

const categoryFormFromDraft = (d: CategoryDraft): CategoryForm => ({
  name: d.name,
  output_dir: d.output_dir ?? "",
  post_processing: d.post_processing,
});

function CategoryEditModal({
  editing,
  existing,
  existingNames,
  onClose,
  onSave,
}: {
  editing: CategoryEditingState;
  existing: CategoryDraft | undefined;
  existingNames: Set<string>;
  onClose: () => void;
  onSave: (d: CategoryDraft) => void;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState<CategoryForm>(blankCategoryForm());

  const open = editing.mode !== "closed";
  const isEdit = editing.mode === "edit";

  useEffect(() => {
    if (editing.mode === "create") setForm(blankCategoryForm());
    if (editing.mode === "edit" && existing) setForm(categoryFormFromDraft(existing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = form.name.trim();
    if (!trimmed) {
      toast("Name is required", "error");
      return;
    }
    if (editing.mode === "create" && existingNames.has(trimmed)) {
      toast(`A category named "${trimmed}" already exists`, "error");
      return;
    }
    if (editing.mode === "create") {
      onSave({
        name: trimmed,
        output_dir: form.output_dir.trim() ? form.output_dir.trim() : null,
        post_processing: form.post_processing,
        _status: "created",
      });
    } else if (existing) {
      onSave({
        ...existing,
        // Name immutable on edit — keep the original.
        name: existing.name,
        output_dir: form.output_dir.trim() ? form.output_dir.trim() : null,
        post_processing: form.post_processing,
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${existing?.name ?? "category"}` : "Add category"}
      width="md"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Name"
          hint={isEdit ? "Cannot be changed after creation" : "e.g. movies, tv, music"}
        >
          <TextInput
            required
            disabled={isEdit}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="movies"
          />
        </Field>
        <Field
          label="Output directory"
          hint="Optional. Leave blank to use the global complete directory."
        >
          <TextInput
            value={form.output_dir}
            onChange={(e) =>
              setForm((f) => ({ ...f, output_dir: e.target.value }))
            }
            placeholder="/data/downloads/movies"
          />
        </Field>
        <Field label="Post-processing">
          <Select
            value={String(form.post_processing)}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                post_processing: Number(e.target.value),
              }))
            }
          >
            {Object.entries(POST_PROCESSING_LABELS).map(([n, label]) => (
              <option key={n} value={n}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">{isEdit ? "Apply" : "Add"}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// GeneralTab
// ---------------------------------------------------------------------------

function GeneralTab({
  draft,
  setDraft,
  loading,
  error,
}: {
  draft: NzbGeneral | null;
  setDraft: React.Dispatch<React.SetStateAction<NzbGeneral | null>>;
  loading: boolean;
  error: unknown;
}) {
  // Local string state for retention so the user can blank the field.
  const [retention, setRetention] = useState<string>("");

  useEffect(() => {
    if (!draft) return;
    setRetention(
      draft.history_retention_count == null
        ? ""
        : String(draft.history_retention_count),
    );
  }, [draft]);

  if (loading) return <p className="text-sm text-foreground/60">Loading…</p>;
  if (error)
    return (
      <EmptyState
        title="NZB downloader is unavailable"
        description={
          error instanceof Error ? `Backend said: ${error.message}` : undefined
        }
      />
    );
  if (!draft) return null;

  const setSpeed = (n: number) =>
    setDraft((d) => (d ? { ...d, speed_limit_bps: n } : d));
  const setMax = (n: number) =>
    setDraft((d) => (d ? { ...d, max_active_downloads: n } : d));
  const setRet = (s: string) => {
    setRetention(s);
    const parsed = s.trim() === "" ? null : Number(s);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) return;
    setDraft((d) => (d ? { ...d, history_retention_count: parsed } : d));
  };

  return (
    <section className="max-w-xl space-y-4">
      <Field
        label="Speed limit (bytes/sec)"
        hint={`0 = unlimited. Currently ${
          draft.speed_limit_bps > 0 ? formatSpeed(draft.speed_limit_bps) : "no limit"
        }.`}
      >
        <NumberInput
          min={0}
          value={draft.speed_limit_bps}
          onChange={(e) => setSpeed(Number(e.target.value) || 0)}
        />
      </Field>
      <Field
        label="Max concurrent downloads"
        hint="Number of NZBs the downloader will work on simultaneously."
      >
        <NumberInput
          min={1}
          value={draft.max_active_downloads}
          onChange={(e) => setMax(Number(e.target.value) || 1)}
        />
      </Field>
      <Field
        label="History retention (count)"
        hint="Number of finished items to keep. Leave blank to keep all."
      >
        <TextInput
          type="number"
          min={0}
          value={retention}
          placeholder="(unlimited)"
          onChange={(e) => setRet(e.target.value)}
        />
      </Field>
      <p className="text-xs text-foreground/40">
        Changes are buffered — click Save in the page header to commit.
      </p>
    </section>
  );
}

// Re-export for type narrowing utility (no-op; keeps the file self-contained).
export type { ServerDraft, CategoryDraft };
// stripDraftFields is currently unused (we build payloads inline) but kept
// available for future tabs that need the same shape.
void stripDraftFields;

// ---------------------------------------------------------------------------
// MediaCategoryMapping
// ---------------------------------------------------------------------------

/**
 * Per-media-kind → download client category. Persists three keys via
 * `/api/v1/settings/download-categories`. The string is passed verbatim
 * to whichever download client picks up the grab — embedded NZB,
 * NZBGet, SAB, qBit. Empty = let the client use its own default.
 *
 * This sits separately from the "Categories" list below: that list
 * configures the embedded engine's per-category output paths +
 * post-processing. The mapping here decides which of those category
 * names arr-in-one tags new grabs with based on what they are.
 */
function MediaCategoryMapping({
  availableCategories,
}: {
  availableCategories: string[];
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ["settings", "download-categories"],
    queryFn: settingsApi.downloadCategories.get,
  });
  const [draft, setDraft] = useState<DownloadCategories>({
    movies: "",
    tv: "",
    music: "",
  });

  useEffect(() => {
    if (data) setDraft(data);
  }, [data?.movies, data?.tv, data?.music]);

  const dirty =
    !!data &&
    (draft.movies !== data.movies ||
      draft.tv !== data.tv ||
      draft.music !== data.music);

  const saveMut = useMutation({
    mutationFn: () => settingsApi.downloadCategories.set(draft),
    onSuccess: (next) => {
      toast("Category mapping saved", "success");
      setDraft(next);
      qc.invalidateQueries({ queryKey: ["settings", "download-categories"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    saveMut.mutate();
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-foreground/80">
          Default category by media kind
        </h3>
        <p className="mt-1 max-w-2xl text-xs text-foreground/60">
          Tag new grabs with one of the categories listed below so the
          download client routes them correctly. Must match a category
          name configured here (for the embedded downloader) or in your
          external client (NZBGet's <code>Category{`{N}`}.Name</code>,
          SAB's drop-down). Leave blank to let the client use its own
          default.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <CategoryPicker
          label="Movies"
          value={draft.movies}
          onChange={(v) => setDraft((d) => ({ ...d, movies: v }))}
          options={availableCategories}
          disabled={isLoading}
        />
        <CategoryPicker
          label="TV"
          value={draft.tv}
          onChange={(v) => setDraft((d) => ({ ...d, tv: v }))}
          options={availableCategories}
          disabled={isLoading}
        />
        <CategoryPicker
          label="Music"
          value={draft.music}
          onChange={(v) => setDraft((d) => ({ ...d, music: v }))}
          options={availableCategories}
          disabled={isLoading}
        />
      </div>

      <div className="flex justify-end">
        <Button
          type="submit"
          size="sm"
          loading={saveMut.isPending}
          disabled={!dirty}
        >
          Save mapping
        </Button>
      </div>
    </form>
  );
}

/**
 * Combo of `<select>` (pick from known categories) and free-form text
 * (type a custom name when the user has external clients with
 * different category names). Renders as a select when there are known
 * categories AND the current value matches one; otherwise a text
 * input that mirrors the value.
 */
function CategoryPicker({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  disabled: boolean;
}) {
  const known = useMemo(() => Array.from(new Set(options)).sort(), [options]);
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <Select
          value={known.includes(value) ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="flex-shrink-0"
        >
          <option value="">— default —</option>
          {known.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <TextInput
          value={value}
          placeholder="or type a custom name"
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      </div>
    </Field>
  );
}
